import { useMemo, useState } from "react";

import {
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    ButtonGroup,
    Button,
    Typography,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import DateRangeIcon from "@mui/icons-material/DateRange";

import { DateTime, DurationObjectUnits } from "luxon";

import { PlaybackRangeDateTimePicker } from "./PlaybackRangeDateTimePicker";
import { PlaybackRangeLastUnitOfTimeDialog } from "./PlaybackRangeLastUnitOfTimeDialog";
import { luxonUtils } from "utils/luxonUtils";

export interface PlaybackRangeControlsProps {
    startDateTime?: string;
    endDateTime?: string;
    startDateTimeError?: string;
    endDateTimeError?: string;
    onRangeChange: (
        startDateTime: DateTime | null,
        endDateTime: DateTime | null,
    ) => void;
}

export function PlaybackRangeControls(props: PlaybackRangeControlsProps) {
    const createPresetRangeChange = (
        timeAmount: number,
        unitOfTime: keyof DurationObjectUnits,
    ) => {
        return () => {
            const endDateTime = luxonUtils.resetSecondsAndMilliseconds(
                DateTime.now(),
            );
            const startDateTime = endDateTime.minus({
                [unitOfTime]: timeAmount,
            });

            props.onRangeChange(startDateTime, endDateTime);
        };
    };

    const [openRangeDialog, setOpenRangeDialog] = useState(false);

    const startDateTimeLuxon = useMemo(
        () =>
            luxonUtils.fromIsoWithFallback(props.startDateTime, DateTime.now()),
        [props.startDateTime],
    );

    const endDateTimeLuxon = useMemo(
        () => luxonUtils.fromIsoWithFallback(props.endDateTime, DateTime.now()),
        [props.endDateTime],
    );

    return (
        <>
            <PlaybackRangeLastUnitOfTimeDialog
                open={openRangeDialog}
                onCancel={() => setOpenRangeDialog(false)}
                onSubmit={(startDateTime, endDateTime) => {
                    setOpenRangeDialog(false);
                    props.onRangeChange(startDateTime, endDateTime);
                }}
            />

            <ListItemButton>
                <ListItemIcon>
                    <DateRangeIcon />
                </ListItemIcon>
                <ListItemText primary="Date range" />
            </ListItemButton>

            <Stack
                sx={{
                    padding: 2,
                }}
            >
                <PlaybackRangeDateTimePicker
                    label="From"
                    value={startDateTimeLuxon}
                    error={props.startDateTimeError}
                    onChange={(value) =>
                        props.onRangeChange(value, endDateTimeLuxon)
                    }
                />
                <PlaybackRangeDateTimePicker
                    label="To"
                    value={endDateTimeLuxon}
                    error={props.endDateTimeError}
                    onChange={(value) =>
                        props.onRangeChange(startDateTimeLuxon, value)
                    }
                />

                <ButtonGroup
                    size="small"
                    variant="outlined"
                    aria-label="outlined primary button group"
                >
                    {/* We need to use onClick of buttons untill this is fixed: https://github.com/mui/material-ui/issues/29514 */}
                    <Button onClick={createPresetRangeChange(15, "minutes")}>
                        <Typography variant="buttonCondensed">
                            15 min
                        </Typography>
                    </Button>
                    <Button onClick={createPresetRangeChange(30, "minutes")}>
                        <Typography variant="buttonCondensed">
                            30 min
                        </Typography>
                    </Button>
                    <Button onClick={createPresetRangeChange(1, "hours")}>
                        <Typography variant="buttonCondensed">
                            1 hour
                        </Typography>
                    </Button>
                    <Button onClick={() => setOpenRangeDialog(true)}>
                        <ReplayIcon />
                    </Button>
                </ButtonGroup>
            </Stack>
        </>
    );
}
